import React, { useState, useEffect } from "react";
import landing from "../images/landing.jpg";
import { useParams } from "react-router-dom";
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import { app } from "../firebase";
import { Typography } from "@material-ui/core";
import {
  getQuestionPoints,
  gradeStoredResponse,
  isOptionCorrect,
  isOptionSelected,
} from "./grading";

function Thankyou() {
  const getEmail = sessionStorage.getItem("email");
  const sessionDisplayName = sessionStorage.getItem("displayName");
  const { id } = useParams();

  const [score, setScore] = useState(null);
  const [isTypingFinished, setIsTypingFinished] = useState(false);
  const [displayText, setDisplayText] = useState("");
  const [thankYouText, setThankYouText] = useState("");
  const [loading, setLoading] = useState(true);

  const [formInfo, setFormInfo] = useState({
    formName: "",
    formDescription: "",
    questions: [],
    activate: "true",
  });

  useEffect(() => {
    const respondentName = score?.userName || sessionDisplayName;
    const anonymousResponse =
      score?.isAnonymous || !getEmail || !respondentName || respondentName === "Anonymous";
    setThankYouText(
      anonymousResponse
        ? "Thank you for your response!"
        : `Thank you for your response, ${respondentName}!`
    );
  }, [getEmail, score?.isAnonymous, score?.userName, sessionDisplayName]);

  useEffect(() => {
    let currentIndex = 0;
    const interval = setInterval(() => {
      setDisplayText(thankYouText.slice(0, currentIndex));
      currentIndex++;
      if (currentIndex > thankYouText.length) {
        setIsTypingFinished(true);
        clearInterval(interval);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [thankYouText]);

  // ==========================================================================
  // FETCH FORM + THE CORRECT RESPONSE  (unchanged logic)
  // ==========================================================================
  useEffect(() => {
    const fetchData = async () => {
      const db = getFirestore(app);
      const formDocRef = doc(db, `forms/${id}`);
      const responsesCollectionRef = collection(
        db,
        `responses/${id}/responses`
      );

      try {
        const formDoc = await getDoc(formDocRef);
        if (formDoc.exists()) {
          setFormInfo(formDoc.data());
        }

        let matchedResponse = null;

        const lastResponseId = sessionStorage.getItem("lastResponseId");
        if (lastResponseId) {
          const exactDocRef = doc(
            db,
            `responses/${id}/responses/${lastResponseId}`
          );
          const exactDocSnap = await getDoc(exactDocRef);
          if (exactDocSnap.exists()) {
            matchedResponse = exactDocSnap.data();
          }
        }

        if (!matchedResponse && getEmail) {
          const byEmailQuery = query(
            responsesCollectionRef,
            where("email", "==", getEmail),
            orderBy("timestamp", "desc"),
            limit(1)
          );
          const byEmailSnapshot = await getDocs(byEmailQuery);
          if (!byEmailSnapshot.empty) {
            matchedResponse = byEmailSnapshot.docs[0].data();
          }
        }

        if (!matchedResponse) {
          const latestResponseQuery = query(
            responsesCollectionRef,
            orderBy("timestamp", "desc"),
            limit(1)
          );
          const latestResponseSnapshot = await getDocs(latestResponseQuery);
          if (!latestResponseSnapshot.empty) {
            matchedResponse = latestResponseSnapshot.docs[0].data();
          }
        }

        setScore(matchedResponse);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, [id, getEmail]);

  if (loading) {
    return (
      <div style={styles.loadingScreen}>
        <div style={styles.spinner} />
        <p style={{ color: "#5f6368", marginTop: 16 }}>Loading your results…</p>
      </div>
    );
  }

  // ==========================================================================
  // GRADE AGAINST THE FROZEN SNAPSHOT, NOT THE LIVE FORM (unchanged logic)
  // ==========================================================================
  const questionsForDisplay =
    score?.ques && Array.isArray(score.ques) && score.ques.length > 0
      ? score.ques
      : formInfo.questions || [];

// ==========================================================================
// REGRADE FROM THE FROZEN SNAPSHOT, DON'T TRUST STORED totalPoints
//
// gradeStoredResponse() re-runs the exact same grading function that was
// used at submission time (see src/grading.js) against the frozen question
// snapshot (`questionsForDisplay`) and the candidate's stored answers.
// This guarantees the score shown here always matches the highlighting
// shown below — the two can no longer drift apart, which is what caused a
// correctly selected answer to be highlighted wrong while a different
// number showed up as "Total Points".
// ==========================================================================
const { totalPoints: recomputedPoints } = gradeStoredResponse(
  questionsForDisplay,
  score?.responses
);

const maxPossiblePoints = questionsForDisplay.reduce(
  (total, question) => total + getQuestionPoints(question),
  0
);

  const normalizedPoints = Math.max(
    0,
    Math.min(Number(recomputedPoints) || 0, maxPossiblePoints)
  );
  const percentage =
    maxPossiblePoints > 0
      ? ((normalizedPoints / maxPossiblePoints) * 100).toFixed(2)
      : "0.00";

  const hasTextBoxQuestion = questionsForDisplay.some(
    (q) => q.questionType === "text"
  );
  const showResult =
    !hasTextBoxQuestion &&
    (formInfo.showResult !== undefined ? formInfo.showResult : true);

  const pct = Number(percentage);
  const ringColor = pct >= 75 ? "#1e7e34" : pct >= 50 ? "#b26a00" : "#c62828";
  const circumference = 2 * Math.PI * 54;
  const dashOffset = circumference - (pct / 100) * circumference;

  return (
    <div style={styles.page}>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes ringFill {
          from { stroke-dashoffset: ${circumference}; }
          to { stroke-dashoffset: ${dashOffset}; }
        }
        .tq-fade { animation: fadeUp 0.5s ease both; }
        .tq-option-row:hover { background: #f8f9fa; }
      `}</style>

      <div style={styles.container} className="tq-fade">
        <span style={styles.thankYouText}>
          {isTypingFinished ? thankYouText : displayText}
        </span>

        {hasTextBoxQuestion && (
          <div style={styles.noticeCard} className="tq-fade">
            <span style={{ fontSize: 20 }}>ℹ️</span>
            <Typography style={styles.noticeText}>
              This test includes text-box questions, so it can't be
              auto-graded. Scores and answer review are disabled for this
              test.
            </Typography>
          </div>
        )}

        {showResult && (
          <div style={styles.scoreRow} className="tq-fade">
            <div style={styles.ringWrapper}>
              <svg width="140" height="140" viewBox="0 0 120 120">
                <circle
                  cx="60"
                  cy="60"
                  r="54"
                  fill="none"
                  stroke="#eceff1"
                  strokeWidth="10"
                />
                <circle
                  cx="60"
                  cy="60"
                  r="54"
                  fill="none"
                  stroke={ringColor}
                  strokeWidth="10"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  style={{
                    animation: "ringFill 1s ease forwards",
                    transform: "rotate(-90deg)",
                    transformOrigin: "60px 60px",
                  }}
                />
              </svg>
              <div style={styles.ringCenter}>
                <span style={{ fontSize: 26, fontWeight: 700, color: ringColor }}>
                  {percentage}%
                </span>
                <span style={{ fontSize: 12, color: "#5f6368" }}>Score</span>
              </div>
            </div>

            <div style={styles.pointsCard}>
              <span style={styles.pointsLabel}>Total Points</span>
              <span style={styles.pointsValue}>
                {normalizedPoints}
                <span style={styles.pointsMax}> / {maxPossiblePoints}</span>
              </span>
            </div>
          </div>
        )}

        {showResult && (
          <div style={styles.reviewCard} className="tq-fade">
            <div style={styles.reviewHeader}>
              <Typography style={styles.formTitle}>
                {formInfo.formName}
              </Typography>
              {formInfo.formDescription && (
                <Typography style={styles.formDescription}>
                  {formInfo.formDescription}
                </Typography>
              )}
            </div>

            <div style={styles.legendRow}>
              <LegendDot color="#1e7e34" label="Correct" />
              <LegendDot color="#c62828" label="Your answer (wrong)" />
              <LegendDot color="#b26a00" label="Missed correct answer" />
            </div>

            {questionsForDisplay.map((question, qIndex) => {
              

              return (
                <div key={qIndex} style={styles.questionBlock}>
                  <Typography style={styles.questionText}>
                    <span style={styles.questionNumber}>{qIndex + 1}</span>
                    {question.questionText}
                  </Typography>

                  <div style={styles.optionsList}>
                    {(question.options || []).map((option, oIndex) => {
                      const userAnswer = score?.responses?.[qIndex];
                      const isUserAnswer = isOptionSelected(userAnswer?.answer, option.optionText);
const isCorrectAnswer = isOptionCorrect(question, option.optionText);
                      let borderColor = "#e0e0e0";
                      let textColor = "#3c4043";
                      let bg = "#fff";
                      let label = "";

                      if (isUserAnswer && isCorrectAnswer) {
                        borderColor = "#1e7e34";
                        textColor = "#1e7e34";
                        bg = "#f1f8f2";
                        label = "Correct answer";
                      } else if (isUserAnswer && !isCorrectAnswer) {
                        borderColor = "#c62828";
                        textColor = "#c62828";
                        bg = "#fdf1f1";
                        label = "Your answer";
                      } else if (!isUserAnswer && isCorrectAnswer) {
                        borderColor = "#b26a00";
                        textColor = "#b26a00";
                        bg = "#fff8ec";
                        label = "Correct — not selected";
                      }

                      return (
                        <label
                          key={oIndex}
                          className="tq-option-row"
                          style={{
                            ...styles.optionRow,
                            borderColor,
                            background: bg,
                          }}
                        >
                          <input
                            type={
                              question.questionType === "checkbox"
                                ? "checkbox"
                                : "radio"
                            }
                            name={`${qIndex}`}
                            value={option.optionText}
                            disabled
                            checked={Boolean(isUserAnswer)}
                            style={{ marginRight: 10 }}
                          />
                          <span style={{ color: textColor, fontWeight: label ? 600 : 400, flex: 1 }}>
                            {option.optionText}
                          </span>
                          {label && (
                            <span style={{ ...styles.optionBadge, color: textColor, borderColor: textColor }}>
                              {label}
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            <div style={styles.brandFooter}>
              <strong style={{ fontSize: 15, letterSpacing: "0.5px" }}>SUREWAY</strong>
              <small style={{ color: "#9aa0a6" }}>by OmneVu.com</small>
            </div>
          </div>
        )}

        <img style={styles.landingImage} src={landing} alt="Landing" />
       
      </div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#5f6368" }}>
      <span style={{ width: 10, height: 10, borderRadius: "50%", background: color, display: "inline-block" }} />
      {label}
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(180deg, #f4f6fb 0%, #ffffff 40%)",
    padding: "64px 16px 40px",
  },
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    maxWidth: 720,
    margin: "0 auto",
  },
  loadingScreen: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  spinner: {
    width: 36,
    height: 36,
    border: "3px solid #e0e0e0",
    borderTopColor: "#4a6cf7",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  thankYouText: {
    fontSize: 21,
    fontWeight: 500,
    lineHeight: 1.5,
    color: "#1d2b42",
    textAlign: "center",
    marginBottom: 36,
  },
  noticeCard: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    backgroundColor: "#fff8ec",
    border: "1px solid #ffcc80",
    borderRadius: 12,
    padding: "12px 18px",
    marginBottom: 24,
    maxWidth: 520,
  },
  noticeText: {
    fontSize: 13,
    color: "#7a4f01",
  },
  scoreRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 28,
    marginBottom: 32,
    flexWrap: "wrap",
  },
  ringWrapper: {
    position: "relative",
    width: 140,
    height: 140,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  ringCenter: {
    position: "absolute",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  pointsCard: {
    background: "#fff",
    borderRadius: 16,
    padding: "20px 28px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 6,
  },
  pointsLabel: {
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    color: "#9aa0a6",
  },
  pointsValue: {
    fontSize: 28,
    fontWeight: 700,
    color: "#202124",
  },
  pointsMax: {
    fontSize: 16,
    fontWeight: 400,
    color: "#9aa0a6",
  },
  reviewCard: {
    width: "100%",
    background: "#fff",
    borderRadius: 20,
    boxShadow: "0 2px 20px rgba(0,0,0,0.06)",
    padding: "28px 28px 20px",
    marginBottom: 32,
  },
  reviewHeader: {
    marginBottom: 12,
  },
  formTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: "#202124",
  },
  formDescription: {
    fontSize: 14,
    color: "#5f6368",
    marginTop: 4,
  },
  legendRow: {
    display: "flex",
    gap: 18,
    flexWrap: "wrap",
    padding: "12px 0",
    borderTop: "1px solid #f0f0f0",
    borderBottom: "1px solid #f0f0f0",
    marginBottom: 20,
  },
  questionBlock: {
    marginBottom: 22,
  },
  questionNumber: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 22,
    height: 22,
    borderRadius: "50%",
    background: "#eef1ff",
    color: "#4a6cf7",
    fontSize: 12,
    fontWeight: 700,
    marginRight: 10,
  },
  questionText: {
    fontSize: 15,
    fontWeight: 500,
    color: "#202124",
    marginBottom: 10,
    display: "flex",
    alignItems: "center",
  },
  optionsList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  optionRow: {
    display: "flex",
    alignItems: "center",
    border: "1.5px solid #e0e0e0",
    borderRadius: 10,
    padding: "10px 14px",
    cursor: "default",
    transition: "background 0.15s ease",
  },
  optionBadge: {
    fontSize: 11,
    fontWeight: 600,
    border: "1px solid",
    borderRadius: 999,
    padding: "2px 8px",
    marginLeft: 10,
    whiteSpace: "nowrap",
  },
  brandFooter: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
    marginTop: 24,
    paddingTop: 16,
    borderTop: "1px solid #f0f0f0",
  },
  landingImage: {
    width: "70%",
    maxWidth: 620,
    height: "auto",
    marginTop: 40,
    borderRadius: 12,
  },
};

export default Thankyou;